import { useState, useRef, useEffect, useCallback } from "react"
import { useDraggable, useDndMonitor } from "@dnd-kit/core"
import { Popover, Tooltip } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation, faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import { getEventCardColors } from "../scheduleEventColorsModel"
import { SpanningEventSegment, WEEK_VIEW_SPANNING_ROW_HEIGHT, SPANNING_EVENT_GAP } from "../MonthView/spanningEventHelpers"
import { DateRangeViewEvent } from "../scheduleApi"
import { RESCHEDULED_EVENT_STRIPE_PATTERN, HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"
import { EventDisplayInfo, isCrewEditable, isEventDragDisabled } from "../scheduleModel"
import EventStatusIcon from "../EventStatusIcon"
import AvatarGroup from "../DayView/AvatarGroup"
import EventSelectCrew from "../EventSelectCrew"
import EventPopoverContent from "../EventPopoverContent"

export type Props = {
  segment: SpanningEventSegment
  eventDisplayInfo: Map<number, EventDisplayInfo>
  dayColumnWidths: number[]
  onEventClick: (eventId: number) => void
  onResize?: (event: DateRangeViewEvent, newEndDate: Dayjs) => void
  onResizingChange?: (isResizing: boolean) => void
  weekStart: Dayjs
}

const WeekViewSpanningEvent = ({ segment, eventDisplayInfo, dayColumnWidths, onEventClick, onResize, onResizingChange, weekStart }: Props) => {
  const { event, startDayIndex, spanWidth, isStart, isEnd, rowIndex, weekIndex } = segment
  const [isHovering, setIsHovering] = useState(false)
  const [elementWidth, setElementWidth] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const justFinishedResizing = useRef(false)
  const lastResizeTransformX = useRef<number>(0)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const showAvatars = elementWidth >= 150

  const displayInfo = eventDisplayInfo.get(event.id)
  const dragDisabled = isEventDragDisabled(event, displayInfo)

  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `spanning-event-${event.id}-${weekIndex}`,
    data: { event, segment },
    disabled: dragDisabled,
  })

  const resizeId = `spanning-event-${event.id}-${weekIndex}-resize`
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    isDragging: isResizing,
    transform: resizeTransform,
  } = useDraggable({
    id: resizeId,
    data: { event, segment, isResize: true },
    disabled: dragDisabled,
  })

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node)
    containerRef.current = node

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
    }

    if (node) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) {
          setElementWidth(entry.contentRect.width)
        }
      })
      resizeObserverRef.current.observe(node)
      setElementWidth(node.offsetWidth)
    }
  }, [setNodeRef])

  const eventEnd = dayjs(event.end)
  const resizeDelta = resizeTransform?.x ?? 0

  useEffect(() => {
    if (resizeTransform?.x !== undefined) {
      lastResizeTransformX.current = resizeTransform.x
    }
  }, [resizeTransform?.x])

  useEffect(() => {
    onResizingChange?.(isResizing)
  }, [isResizing, onResizingChange])

  useDndMonitor({
    onDragEnd: (dragEvent) => {
      if (dragEvent.active.id !== resizeId) {
        return
      }
      justFinishedResizing.current = true
      if (onResize && dayColumnWidths.length > 0) {
        const totalDelta = lastResizeTransformX.current
        const absDelta = Math.abs(totalDelta)
        const endDayIdx = segment.endDayIndex

        const columns = totalDelta >= 0
          ? dayColumnWidths.slice(endDayIdx + 1, 7)
          : dayColumnWidths.slice(startDayIndex + 1, endDayIdx + 1).reverse()

        let cumulativeWidth = 0
        const dayCount = columns.reduce((days, width) => {
          if (cumulativeWidth + width / 2 <= absDelta) {
            cumulativeWidth += width
            return days + 1
          }
          return days
        }, 0)

        const deltaDays = totalDelta >= 0 ? dayCount : -dayCount
        const newEndDate = eventEnd.add(deltaDays, 'day')
        const eventStart = dayjs(event.start)
        if (!newEndDate.isBefore(eventStart, 'day') && deltaDays !== 0) {
          onResize(event, newEndDate)
        }
      }
      lastResizeTransformX.current = 0
    },
  })

  const { backgroundColor, palette } = getEventCardColors(event.crewForemanColor)

  const leftPx = dayColumnWidths.slice(0, startDayIndex).reduce((s, w) => s + w, 0)
  let widthPx = dayColumnWidths.slice(startDayIndex, startDayIndex + spanWidth).reduce((s, w) => s + w, 0)

  if (isResizing && dayColumnWidths.length > 0) {
    const absResizeDelta = Math.abs(resizeDelta)
    const originalEndDayIndex = segment.endDayIndex

    const columns = resizeDelta >= 0
      ? dayColumnWidths.slice(originalEndDayIndex + 1, 7)
      : dayColumnWidths.slice(startDayIndex + 1, originalEndDayIndex + 1).reverse()

    let cumulativeWidth = 0
    const dayCount = columns.reduce((days, width) => {
      if (cumulativeWidth + width / 2 <= absResizeDelta) {
        cumulativeWidth += width
        return days + 1
      }
      return days
    }, 0)

    const deltaDays = resizeDelta >= 0 ? dayCount : -dayCount
    const eventStart = dayjs(event.start)
    let newEndDayIndex = originalEndDayIndex + deltaDays

    const minEndDayIndex = Math.max(startDayIndex, eventStart.diff(weekStart.startOf('day'), 'day'))
    newEndDayIndex = Math.max(newEndDayIndex, minEndDayIndex)
    newEndDayIndex = Math.min(newEndDayIndex, 6)

    const newSpanWidth = newEndDayIndex - startDayIndex + 1
    widthPx = dayColumnWidths.slice(startDayIndex, startDayIndex + newSpanWidth).reduce((s, w) => s + w, 0)
  }

  const top = rowIndex * (WEEK_VIEW_SPANNING_ROW_HEIGHT + SPANNING_EVENT_GAP)
  const margin = 2

  const isRescheduled = event.rescheduledToEventId != null

  const style: React.CSSProperties = {
    position: "absolute",
    top: top + margin,
    left: leftPx + margin,
    width: widthPx - margin * 2,
    height: WEEK_VIEW_SPANNING_ROW_HEIGHT - margin * 2,
    display: "flex",
    alignItems: "stretch",
    gap: 4,
    padding: "4px 8px",
    borderRadius: isStart && isEnd ? 4 : isStart ? "4px 0 0 4px" : isEnd ? "0 4px 4px 0" : 0,
    backgroundColor,
    color: palette.primaryText,
    cursor: isResizing ? "ew-resize" : "pointer",
    overflow: "hidden",
    boxSizing: "border-box",
    zIndex: isDragging || isResizing ? 100 : 1,
    opacity: isDragging ? 0.5 : 1,
    transform: !isResizing && transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    pointerEvents: "auto",
    backgroundImage: isRescheduled ? RESCHEDULED_EVENT_STRIPE_PATTERN : undefined,
  }

  const crewEditable = isCrewEditable(displayInfo)

  const dragProps = isResizing ? {} : { ...listeners, ...attributes }

  return (
    <Popover
      content={
        <EventPopoverContent
          event={event}
          workareaStatuses={eventDisplayInfo.get(event.id)?.workareaStatuses}
          timesheets={eventDisplayInfo.get(event.id)?.timesheets}
          warning={eventDisplayInfo.get(event.id)?.warning ?? undefined}
          rescheduledToDate={eventDisplayInfo.get(event.id)?.rescheduledToDate}
        />
      }
      open={isHovering && !isDragging && !isResizing}
      placement="bottom"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setRefs}
        {...dragProps}
        onClick={(e) => {
          e.stopPropagation()
          if (justFinishedResizing.current) {
            justFinishedResizing.current = false
            return
          }
          if (!isResizing) {
            onEventClick(event.id)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isResizing) {
            setIsHovering(false)
          }
        }}
        style={style}
      >
        {!isStart && (
          <span style={{ color: palette.secondaryText, fontSize: 10, flexShrink: 0, alignSelf: 'center' }}>◀</span>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {displayInfo && showAvatars && (
              <EventStatusIcon status={displayInfo.status} />
            )}
            {displayInfo?.warning && (
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fa8c16', fontSize: 12, flexShrink: 0 }} />
            )}
            {event.frozen && (
              <Tooltip title="Frozen">
                <FontAwesomeIcon icon={faSnowflake} style={{ color: palette.secondaryText, fontSize: 12, flexShrink: 0 }} />
              </Tooltip>
            )}
            <span
              style={{
                fontWeight: 500,
                fontSize: 12,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
                textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined,
              }}
            >
              {[event.customer?.displayName, event.jobName].filter(Boolean).join(' · ')}
            </span>
            {showAvatars && (
              <div style={{ flexShrink: 0 }}>
                {crewEditable
                  ? <EventSelectCrew event={event} variant="avatar" color={palette.primaryText} />
                  : <AvatarGroup crewForemanEmail={event.crewForemanEmail} crewMemberIds={event.crewMemberIds} initialsColor={palette.primaryText} />
                }
              </div>
            )}
          </div>
          <span style={{ color: palette.secondaryText, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            All Day
          </span>
        </div>
        {!isEnd && !isResizing && (
          <span style={{ color: palette.secondaryText, fontSize: 10, flexShrink: 0, alignSelf: 'center' }}>▶</span>
        )}

        {isEnd && (isHovering || isResizing) && onResize && (
          <div
            ref={setResizeNodeRef}
            {...resizeListeners}
            {...resizeAttributes}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: 8,
              cursor: "ew-resize",
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 3,
                height: 12,
                backgroundColor: palette.primaryText,
                borderRadius: 2,
              }}
            />
          </div>
        )}
      </div>
    </Popover>
  )
}

export default WeekViewSpanningEvent
